import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { message } from 'antd';
import { API_CONFIG, APP_CONFIG } from '../config';

// 创建axios实例
const apiClient: AxiosInstance = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 从localStorage获取token
    const token = localStorage.getItem(APP_CONFIG.tokenKey);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // 开发环境打印请求日志
    if (API_CONFIG.enableRequestLog) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, config.data || config.params);
    }
    
    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // 开发环境打印响应日志
    if (API_CONFIG.enableResponseLog) {
      console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data);
    }
    
    // 返回完整的响应对象，包含原始的axios响应
    return response;
  },
  (error) => {
    const errorConfig = error.config;
    
    if (error.response) {
      const { status, data } = error.response;
      
      // 开发环境打印错误日志
      if (API_CONFIG.enableRequestLog) {
        console.error(`[API Error] ${status} ${errorConfig?.url}`, data);
      }
      
      // 处理401未授权
      if (status === 401) {
        localStorage.removeItem(APP_CONFIG.tokenKey);
        localStorage.removeItem(APP_CONFIG.refreshTokenKey);
        localStorage.removeItem(APP_CONFIG.userKey);
        message.error('登录已过期，请重新登录');
        window.location.href = '/login';
      } else if (status >= 500) {
        message.error('服务器错误，请稍后重试');
      } else if (status === 403) {
        message.error('没有权限访问该资源');
      } else if (status === 404) {
        message.error('请求的资源不存在');
      }
      
      return Promise.reject({
        success: false,
        message: data?.message || '请求失败',
        code: status,
        error: data?.error,
      });
    } else if (error.request) {
      // 请求已发出但没有收到响应
      console.error('[API Network Error]', error.message);
      message.error('网络错误，请检查网络连接');
      return Promise.reject({
        success: false,
        message: '网络错误',
        code: 0,
        error: error.message,
      });
    } else {
      // 请求配置出错
      console.error('[API Config Error]', error.message);
      return Promise.reject({
        success: false,
        message: error.message || '请求配置错误',
        code: 0,
        error: error.message,
      });
    }
  }
);

// 通用请求方法
const request = async <T = any>(
  config: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return await apiClient.request<T>(config);
};

/**
 * 更新API基础URL
 * 用于环境切换时动态更新API地址
 */
export const updateApiBaseUrl = (baseUrl: string): void => {
  apiClient.defaults.baseURL = baseUrl;
  console.log(`[API] BaseURL updated to: ${baseUrl}`);
};

/**
 * 获取当前API基础URL
 */
export const getCurrentApiBaseUrl = (): string => {
  return apiClient.defaults.baseURL as string;
};

export { apiClient, request };
export default apiClient;